import React, { useState, useMemo, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const API_URL = import.meta.env.VITE_API_URL;
const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444"];

export default function FinancialDashboard() {
  /* ================= STATES ================= */
  const [income, setIncome] = useState("");
  const [cryptoAssets, setCryptoAssets] = useState([]);
  const [stockAssets, setStockAssets] = useState([]);
  const [dollar, setDollar] = useState("");
  const [gold, setGold] = useState("");
  const [expenses, setExpenses] = useState([]);

  const [newCrypto, setNewCrypto] = useState({ symbol: "", amount: "" });
  const [newStock, setNewStock] = useState({ symbol: "", amount: "" });
  const [newExpense, setNewExpense] = useState({ category: "", amount: "" });

  const [editing, setEditing] = useState(null); // { type: 'crypto' | 'stock' | 'expense', id }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const toNumber = (v) => (v === "" || isNaN(Number(v)) ? 0 : Number(v));

  const sectionClass =
    "p-5 border rounded-xl bg-white shadow-sm hover:shadow-md transition mb-6";

  /* ================= LOAD DATA FROM SERVER ================= */
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await fetch(`${API_URL}/dashboard`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include"
        });

        if (!res.ok) {
          if (res.status === 401) {
            alert("⚠️ لطفاً اول وارد حساب کاربری شوید.");
            window.location.href = "/login";
            return;
          }
          throw new Error("خطا در دریافت داده‌ها");
        }

        const data = await res.json();

        if (data.exists && data.dashboard) {
          const d = data.dashboard;
          setIncome(d.income || "");
          setCryptoAssets(d.cryptoAssets || []);
          setStockAssets(d.stockAssets || []);
          setDollar(d.dollar || "");
          setGold(d.gold || "");
          setExpenses(d.expenses || []);
        }
      } catch (err) {
        console.error("خطا در بارگذاری داشبورد:", err);
        alert("❌ نتوانستیم داده‌های شما را بارگذاری کنیم. بعداً دوباره امتحان کنید.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  /* ================= SAVE TO SERVER ================= */
  const saveToServer = async () => {
    setSaving(true);
    const payload = {
      income: income || null,
      cryptoAssets: cryptoAssets.map((c) => ({
        id: c.id,
        symbol: c.symbol,
        amount: toNumber(c.amount),
      })),
      stockAssets: stockAssets.map((s) => ({
        id: s.id,
        symbol: s.symbol,
        amount: toNumber(s.amount),
      })),
      dollar: dollar || null,
      gold: gold || null,
      expenses: expenses.map((e) => ({
        id: e.id,
        category: e.category,
        amount: toNumber(e.amount),
      })),
    };

    try {
      const res = await fetch(`${API_URL}/dashboard`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        alert("✅ داشبورد با موفقیت ذخیره شد!");
      } else {
        const err = await res.json();
        alert("❌ خطا در ذخیره: " + (err.error || "نامشخص"));
      }
    } catch (err) {
      console.error("خطا در ارسال به سرور:", err);
      alert("❌ خطا در ارتباط با سرور");
    } finally {
      setSaving(false);
    }
  };

  /* ================= ADD FUNCTIONS ================= */
  const addCrypto = () => {
    if (!newCrypto.symbol.trim() || toNumber(newCrypto.amount) === 0) return;
    setCryptoAssets([
      ...cryptoAssets,
      { id: Date.now(), ...newCrypto, amount: toNumber(newCrypto.amount) },
    ]);
    setNewCrypto({ symbol: "", amount: "" });
  };

  const addStock = () => {
    if (!newStock.symbol.trim() || toNumber(newStock.amount) === 0) return;
    setStockAssets([
      ...stockAssets,
      { id: Date.now(), ...newStock, amount: toNumber(newStock.amount) },
    ]);
    setNewStock({ symbol: "", amount: "" });
  };

  const addExpense = () => {
    if (!newExpense.category.trim() || toNumber(newExpense.amount) === 0) return;
    setExpenses([
      ...expenses,
      { id: Date.now(), ...newExpense, amount: toNumber(newExpense.amount) },
    ]);
    setNewExpense({ category: "", amount: "" });
  };

  /* ================= DELETE & EDIT ================= */
  const removeItem = (list, setList, id) => {
    setList(list.filter((i) => i.id !== id));
  };

  const startEdit = (type, id) => setEditing({ type, id });
  const cancelEdit = () => setEditing(null);

  const saveEdit = () => {
    cancelEdit();
  };

  /* ================= NET WORTH ================= */
  const netWorth = useMemo(() => {
    const crypto = cryptoAssets.reduce((s, c) => s + toNumber(c.amount), 0);
    const stocks = stockAssets.reduce((s, s2) => s + toNumber(s2.amount), 0);
    const usd = toNumber(dollar);
    const goldValue = toNumber(gold);

    return {
      total: crypto + stocks + usd + goldValue,
      crypto,
      stocks,
      usd,
      gold: goldValue,
    };
  }, [cryptoAssets, stockAssets, dollar, gold]);

  const pieData = [
    { name: "کریپتو", value: netWorth.crypto },
    { name: "بورس", value: netWorth.stocks },
    { name: "دلار", value: netWorth.usd },
    { name: "طلا", value: netWorth.gold },
  ].filter((i) => i.value > 0);

  /* ================= RENDER ================= */
  if (loading) {
    return (
      <div dir="rtl" className="p-6 max-w-5xl mx-auto text-center mt-20">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-emerald-600"></div>
        <p className="mt-6 text-lg text-gray-600">در حال بارگذاری داشبورد شما...</p>
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      className="p-6 max-w-5xl mx-auto bg-gray-50 min-h-screen font-[Vazirmatn]"
    >
      <h1 className="text-4xl font-bold mb-6 text-center">
        داشبورد مالی تریدر
      </h1>

      {/* دکمه ذخیره */}
      <div className="text-center mb-8">
        <button
          onClick={saveToServer}
          disabled={saving}
          className={`px-8 py-4 text-lg font-bold text-white rounded-xl shadow-lg transition ${
            saving
              ? "bg-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
          }`}
        >
          {saving ? "در حال ذخیره..." : "💾 ذخیره تغییرات در سرور"}
        </button>
      </div>

      {/* INCOME */}
      <div className={sectionClass}>
        <h2 className="font-semibold mb-3">💼 درآمد ماهانه</h2>
        <input
          type="number"
          placeholder="درآمد ماهانه به تومان"
          className="border p-3 w-full rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          value={income}
          onChange={(e) => setIncome(e.target.value)}
        />
        {income && (
          <p className="mt-2 text-sm text-gray-600">
            درآمد ثبت‌شده: {Number(income).toLocaleString()} تومان
          </p>
        )}
      </div>

      {/* NET WORTH */}
      <div className="mb-8 p-6 rounded-2xl bg-gradient-to-l from-emerald-500 to-emerald-700 text-white text-center shadow-xl">
        <p className="text-sm opacity-80 mb-1">ارزش کل سرمایه</p>
        <h2 className="text-5xl font-extrabold mb-4">
          {netWorth.total.toLocaleString()} تومان
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>🪙 کریپتو: {netWorth.crypto.toLocaleString()} تومان</div>
          <div>📈 بورس: {netWorth.stocks.toLocaleString()} تومان</div>
          <div>💵 دلار: {netWorth.usd.toLocaleString()} تومان</div>
          <div>🪙 طلا: {netWorth.gold.toLocaleString()} تومان</div>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className={sectionClass}>
          <h3 className="font-semibold mb-4 text-center">توزیع سرمایه</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  label={({ name, value }) => `${name}: ${value.toLocaleString()}`}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v.toLocaleString()} تومان`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500">هنوز دارایی ثبت نشده</p>
          )}
        </div>

        <div className={sectionClass}>
          <h3 className="font-semibold mb-4 text-center">هزینه‌ها</h3>
          {expenses.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={expenses.map((e) => ({ ...e, amount: toNumber(e.amount) }))}>
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip formatter={(v) => `${v.toLocaleString()} $`} />
                <Bar dataKey="amount" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500">هنوز هزینه‌ای ثبت نشده</p>
          )}
        </div>
      </div>

      {/* CRYPTO */}
      <div className={sectionClass}>
        <h2 className="font-semibold mb-3">🪙 کریپتو</h2>
        <div className="flex gap-2 mb-3">
          <input
            placeholder="مثل BTC"
            className="border p-2 rounded w-1/2 focus:ring-2 focus:ring-green-500"
            value={newCrypto.symbol}
            onChange={(e) => setNewCrypto({ ...newCrypto, symbol: e.target.value.toUpperCase() })}
          />
          <input
            type="number"
            placeholder="ارزش تومانی"
            className="border p-2 rounded w-1/2 focus:ring-2 focus:ring-green-500"
            value={newCrypto.amount}
            onChange={(e) => setNewCrypto({ ...newCrypto, amount: e.target.value })}
          />
        </div>
        <button onClick={addCrypto} className="px-4 py-2 bg-green-600 text-white rounded mb-3 hover:bg-green-700">
          افزودن کریپتو
        </button>

        <ul className="space-y-2 text-sm">
          {cryptoAssets.map((c) => (
            <li key={c.id} className="p-3 border rounded bg-gray-50 flex justify-between items-center">
              {editing?.type === "crypto" && editing?.id === c.id ? (
                <>
                  <input
                    className="border p-1 rounded w-1/3"
                    value={c.symbol}
                    onChange={(e) =>
                      setCryptoAssets(cryptoAssets.map((x) =>
                        x.id === c.id ? { ...x, symbol: e.target.value.toUpperCase() } : x
                      ))
                    }
                  />
                  <input
                    type="number"
                    className="border p-1 rounded w-1/3"
                    value={c.amount}
                    onChange={(e) =>
                      setCryptoAssets(cryptoAssets.map((x) =>
                        x.id === c.id ? { ...x, amount: e.target.value } : x
                      ))
                    }
                  />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="text-green-600 text-xl">💾</button>
                    <button onClick={cancelEdit} className="text-gray-600 text-xl">✖️</button>
                  </div>
                </>
              ) : (
                <>
                  <span className="font-medium">{c.symbol}</span>
                  <span>{toNumber(c.amount).toLocaleString()} تومان</span>
                  <div className="flex gap-3">
                    <button onClick={() => startEdit("crypto", c.id)} className="text-blue-600">✏️</button>
                    <button onClick={() => removeItem(cryptoAssets, setCryptoAssets, c.id)} className="text-red-600">❌</button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* STOCK */}
      <div className={sectionClass}>
        <h2 className="font-semibold mb-3">📈 سهام</h2>
        <div className="flex gap-2 mb-3">
          <input
            placeholder="نماد"
            className="border p-2 rounded w-1/2 focus:ring-2 focus:ring-blue-500"
            value={newStock.symbol}
            onChange={(e) => setNewStock({ ...newStock, symbol: e.target.value })}
          />
          <input
            type="number"
            placeholder="ارزش تومانی"
            className="border p-2 rounded w-1/2 focus:ring-2 focus:ring-blue-500"
            value={newStock.amount}
            onChange={(e) => setNewStock({ ...newStock, amount: e.target.value })}
          />
        </div>
        <button onClick={addStock} className="px-4 py-2 bg-blue-600 text-white rounded mb-3 hover:bg-blue-700">
          افزودن سهام
        </button>

        <ul className="space-y-2 text-sm">
          {stockAssets.map((s) => (
            <li key={s.id} className="p-3 border rounded bg-gray-50 flex justify-between items-center">
              {editing?.type === "stock" && editing?.id === s.id ? (
                <>
                  <input
                    className="border p-1 rounded w-1/3"
                    value={s.symbol}
                    onChange={(e) =>
                      setStockAssets(stockAssets.map((x) =>
                        x.id === s.id ? { ...x, symbol: e.target.value } : x
                      ))
                    }
                  />
                  <input
                    type="number"
                    className="border p-1 rounded w-1/3"
                    value={s.amount}
                    onChange={(e) =>
                      setStockAssets(stockAssets.map((x) =>
                        x.id === s.id ? { ...x, amount: e.target.value } : x
                      ))
                    }
                  />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="text-green-600 text-xl">💾</button>
                    <button onClick={cancelEdit} className="text-gray-600 text-xl">✖️</button>
                  </div>
                </>
              ) : (
                <>
                  <span className="font-medium">{s.symbol}</span>
                  <span>{toNumber(s.amount).toLocaleString()} تومان</span>
                  <div className="flex gap-3">
                    <button onClick={() => startEdit("stock", s.id)} className="text-blue-600">✏️</button>
                    <button onClick={() => removeItem(stockAssets, setStockAssets, s.id)} className="text-red-600">❌</button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* DOLLAR & GOLD */}
      <div className={sectionClass}>
        <h2 className="font-semibold mb-3">💵 دلار و طلا</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm mb-1 font-medium">دلار نقدی</label>
            <input
              type="number"
              placeholder="مقدار دلار"
              className="border p-3 rounded w-full focus:ring-2 focus:ring-yellow-500"
              value={dollar}
              onChange={(e) => setDollar(e.target.value)}
            />
            <button onClick={() => setDollar("")} className="text-xs text-red-500 mt-2 block">
              پاک کردن
            </button>
          </div>
          <div>
            <label className="block text-sm mb-1 font-medium">طلا (ارزش تومانی)</label>
            <input
              type="number"
              placeholder="ارزش طلا به دلار"
              className="border p-3 rounded w-full focus:ring-2 focus:ring-yellow-500"
              value={gold}
              onChange={(e) => setGold(e.target.value)}
            />
            <button onClick={() => setGold("")} className="text-xs text-red-500 mt-2 block">
              پاک کردن
            </button>
          </div>
        </div>
      </div>

      {/* EXPENSES */}
      <div className={sectionClass}>
        <h2 className="font-semibold mb-3">💸 هزینه‌ها</h2>
        <div className="flex gap-2 mb-3">
          <input
            placeholder="دسته (مثل غذا، حمل‌ونقل)"
            className="border p-2 rounded w-1/2 focus:ring-2 focus:ring-purple-500"
            value={newExpense.category}
            onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
          />
          <input
            type="number"
            placeholder="مبلغ"
            className="border p-2 rounded w-1/2 focus:ring-2 focus:ring-purple-500"
            value={newExpense.amount}
            onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
          />
        </div>
        <button onClick={addExpense} className="px-4 py-2 bg-purple-600 text-white rounded mb-3 hover:bg-purple-700">
          افزودن هزینه
        </button>

        <ul className="space-y-2 text-sm">
          {expenses.map((e) => (
            <li key={e.id} className="p-3 border rounded bg-gray-50 flex justify-between items-center">
              {editing?.type === "expense" && editing?.id === e.id ? (
                <>
                  <input
                    className="border p-1 rounded w-1/3"
                    value={e.category}
                    onChange={(ev) =>
                      setExpenses(expenses.map((x) =>
                        x.id === e.id ? { ...x, category: ev.target.value } : x
                      ))
                    }
                  />
                  <input
                    type="number"
                    className="border p-1 rounded w-1/3"
                    value={e.amount}
                    onChange={(ev) =>
                      setExpenses(expenses.map((x) =>
                        x.id === e.id ? { ...x, amount: ev.target.value } : x
                      ))
                    }
                  />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="text-green-600 text-xl">💾</button>
                    <button onClick={cancelEdit} className="text-gray-600 text-xl">✖️</button>
                  </div>
                </>
              ) : (
                <>
                  <span className="font-medium">{e.category}</span>
                  <span>{toNumber(e.amount).toLocaleString()} تومان</span>
                  <div className="flex gap-3">
                    <button onClick={() => startEdit("expense", e.id)} className="text-blue-600">✏️</button>
                    <button onClick={() => removeItem(expenses, setExpenses, e.id)} className="text-red-600">❌</button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* پیام پایینی */}
      <div className="text-center mt-12 text-sm text-gray-500">
        تغییرات به‌صورت محلی اعمال می‌شوند. برای ذخیره دائمی حتماً روی دکمه بالای صفحه کلیک کنید.
      </div>
    </div>
  );
}